<ng-container *transloco="let t; read: 'providers.feedPage.gtfs.staticPane'">
  <div class="flex flex-col gap-5">
    <!-- Source of the feed's GTFS zip -->
    <div class="flex flex-col gap-3">
      <div class="flex flex-col gap-1">
        <span class="text-[11px] font-bold uppercase tracking-wide text-ink-3">{{ t('source.title') }}</span>
        <p class="m-0 max-w-[620px] text-[13px] leading-5 text-ink-2">{{ t('source.intro') }}</p>
      </div>
      <app-feed-file-list
        [feedId]="feedId()"
        [maxFiles]="1"
        [overLimitWarning]="t('source.overLimit')"
        defaultFilename="gtfs.zip"
        accept=".zip,application/zip"
        [quickStart]="true"
        (changed)="onFilesChanged()"
      />
    </div>

    <div class="flex flex-col gap-1 border-t border-hairline pt-5">
      <span class="text-[11px] font-bold uppercase tracking-wide text-ink-3">{{ t('sets.title') }}</span>
      <p class="m-0 max-w-[620px] text-[13px] leading-5 text-ink-2">{{ t('intro') }}</p>
    </div>
    <!-- Delivered sets -->
    <div class="flex flex-col">
      <div class="grid h-8 grid-cols-[116px_minmax(0,1fr)_150px_80px_48px] items-center rounded-[4px] bg-sunken px-3 text-[12px] font-semibold text-ink-2">
        <span>{{ t('columns.status') }}</span>
        <span>{{ t('columns.file') }}</span>
        <span>{{ t('columns.uploaded') }}</span>
        <span class="text-right">{{ t('columns.size') }}</span>
        <span></span>
      </div>

      @if (isLoading()) {
        <span class="px-2 py-5 text-[13px] text-ink-3">—</span>
      } @else {
        @for (set of fileSets(); track set.id) {
          @let isLive = set.id === liveFileSetId();
          <div class="shadow-[inset_0_-1px_0_var(--color-hairline)]">
            <div
              class="grid grid-cols-[116px_minmax(0,1fr)_150px_80px_48px] items-center px-3 py-3"
              [class.min-h-16]="!set.statusMessage && set.status !== 'PENDING'"
              [class.min-h-[72px]]="!!set.statusMessage || set.status === 'PENDING'"
            >
              <span>
                <app-label-badge [dot]="statusDot(set.status)">{{ t('status.' + set.status) }}</app-label-badge>
              </span>

              <div class="flex min-w-0 flex-col gap-0.5 pr-4">
                <div class="flex min-w-0 items-center gap-2" [attr.title]="set.files.length > 1 ? fileList(set) : set.files[0]?.filename">
                  <span class="truncate text-[14px] text-ink-1">{{ set.files[0]?.filename }}</span>
                  @if (set.files.length > 1) {
                    <span class="flex-none text-[12px] text-ink-3">{{ t('moreFiles', { count: set.files.length - 1 }) }}</span>
                  }
                  @if (isLive) {
                    <span class="flex-none text-[11px] font-bold uppercase text-success">{{ t('live', { version: liveVersion() ?? '?' }) }}</span>
                  }
                </div>
                @if (set.status === 'FAILED' && set.statusMessage) {
                  <span class="text-[12px] leading-[18px] text-danger">{{ set.statusMessage }}</span>
                } @else if (set.status === 'PENDING') {
                  <span class="text-[12px] leading-[18px] text-ink-2">{{ t('pendingMessage') }}</span>
                } @else if (set.statusMessage) {
                  <span class="truncate text-[12px] leading-[18px] text-ink-2">{{ set.statusMessage }}</span>
                }
              </div>

              <div class="flex flex-col">
                <span class="text-[13px] text-ink-1">{{ relativeTime(set.createdAt) }}</span>
                <span class="text-[12px] text-ink-3">{{ set.createdAt | date: 'mediumDate' }}</span>
              </div>

              <span class="text-right text-[13px] text-ink-2">{{ formatBytes(setBytes(set)) }}</span>

              <div class="flex justify-end">
                <button
                  type="button"
                  (click)="onDownloadClick(set)"
                  [disabled]="set.files.length === 1 && downloadingFileId() === set.files[0].id"
                  [attr.aria-label]="set.files.length > 1 ? t('showFiles') : t('download')"
                  [attr.aria-expanded]="set.files.length > 1 ? isExpanded(set) : null"
                  class="flex h-8 w-8 items-center justify-center rounded-control text-ink-2 transition-colors duration-150 hover:bg-sunken hover:text-ink-1 disabled:opacity-40"
                >
                  <app-icon name="icon-export" [size]="24" />
                </button>
              </div>
            </div>

            <!-- Multi-file set: per-file download links -->
            @if (set.files.length > 1 && isExpanded(set)) {
              <div class="flex flex-col gap-1 pb-3 pl-[128px] pr-3">
                @for (file of set.files; track file.id) {
                  <div class="flex items-center gap-3 text-[13px]">
                    <span class="min-w-0 flex-1 truncate text-ink-1">{{ file.filename }}</span>
                    @if (file.role) {
                      <span class="flex-none text-[12px] text-ink-3">{{ file.role }}</span>
                    }
                    <span class="w-[72px] flex-none text-right text-ink-2">{{ formatBytes(file.size) }}</span>
                    <button
                      type="button"
                      (click)="download(set, file)"
                      [disabled]="downloadingFileId() === file.id"
                      class="flex-none font-semibold text-link hover:underline disabled:opacity-40"
                    >
                      {{ t('download') }}
                    </button>
                  </div>
                }
              </div>
            }
          </div>
        } @empty {
          <span class="px-2 py-5 text-[13px] text-ink-3">{{ t('empty') }}</span>
        }
      }
    </div>
  </div>
</ng-container>
